/* eslint-disable etc/no-commented-out-code */
// Distance functions! https://iquilezles.org/articles/distfunctions2d/
import { PrimitiveType, } from "@buildwithflux/models";
import { mat3, geom2, vec2, ZeroVector2 } from "../../../common";
import { approxFittingPolygon } from "../math/approxFittingPolygon";
/**
 * Gets the minimum distance between the border of the shapes and a point (negative if inside)
 * NOTE: Not all the shapes actually modify the closestPoint, as it's still a TODO
 */
export function pointDistance(primitive, pointX, pointY, polygonApproxSegments = 128) {
    // Cases for which there is no need to worry about scaling (closestPoint implemented)
    if (primitive.type === PrimitiveType.Circle) {
        return circlePointDistance(primitive, pointX, pointY);
    }
    else if (primitive.type === PrimitiveType.Rectangle) {
        return rectanglePointDistance(primitive, pointX, pointY);
    }
    else if (primitive.type === PrimitiveType.Union) {
        return unionPointDistance(primitive, pointX, pointY);
    }
    // Cases that only work if the scale is equal in both axes
    // This check can go away when we implement closestPoint (we can use it for computing the distance)
    const transformDecomposed = mat3.decompose(primitive.transform);
    const matrixIsSimple = mat3.affineSanityCheck(primitive.transform) &&
        transformDecomposed.scaleIsEqual &&
        transformDecomposed.xyDot === 0;
    if (matrixIsSimple && primitive.type !== PrimitiveType.ApproxPolygon) {
        const scale = transformDecomposed ? Math.abs(transformDecomposed.scale.x) : 1;
        switch (primitive.type) {
            case PrimitiveType.Oblong:
                return oblongPointDistance(primitive, pointX, pointY, scale);
            case PrimitiveType.RoundedRectangle:
                return roundedRectanglePointDistance(primitive, pointX, pointY, scale);
            case PrimitiveType.Line:
                return linePointDistance(primitive, pointX, pointY, scale);
            case PrimitiveType.Difference:
                return differencePointDistance(primitive, pointX, pointY, scale);
            case PrimitiveType.Expansion:
                return expansionPointDistance(primitive, pointX, pointY, scale);
        }
    }
    // Approximate the shape as a polygon as fallback
    const polygon = approxFittingPolygon(primitive, polygonApproxSegments);
    const distance = geom2.polygonPointDistance({ x: pointX, y: pointY }, polygon);
    return distance;
}
function circlePointDistance(primitive, pointX, pointY) {
    const point = { x: pointX, y: pointY };
    const pointToPrimitive = vec2.applyMatrix3(point, primitive.transformInverse);
    const sign = vec2.len(pointToPrimitive) < primitive.radius ? -1 : 1;
    const closestPoint = vec2
        .chain(pointToPrimitive)
        .normalize()
        ?.scale(primitive.radius)
        .applyMatrix3(primitive.transform)
        .xy();
    return { dist: (closestPoint ? vec2.distance(closestPoint, point) : 0) * sign, outPoint: closestPoint };
}
function rectanglePointDistance(primitive, pointX, pointY) {
    // It's more convenient to just use the polygon way, since it's 4 points anyway
    const polygon = approxFittingPolygon(primitive, 0);
    const originalP = { x: pointX, y: pointY };
    return geom2.polygonPointDistance(originalP, polygon);
}
function oblongPointDistance(primitive, pointX, pointY, scale) {
    // Normal circle
    if (primitive.width === primitive.height) {
        return circlePointDistance({ radius: primitive.width, transform: primitive.transform, transformInverse: primitive.transformInverse }, pointX, pointY);
    }
    const localP = vec2.applyMatrix3({ x: pointX, y: pointY }, primitive.transformInverse);
    // Consider the oblong as a very rounded rectangle
    const capRadius = Math.min(primitive.width, primitive.height) / 2;
    const d = vec2.abs(localP);
    d.x -= primitive.width / 2;
    d.y -= primitive.height / 2;
    d.x += capRadius;
    d.y += capRadius;
    const dist = Math.min(Math.max(d.x, d.y), 0) + vec2.len(vec2.max(d, ZeroVector2)) - capRadius;
    return { dist: dist / scale };
    // TODO closestPoint
}
function roundedRectanglePointDistance(primitive, pointX, pointY, scale) {
    const p = vec2.applyMatrix3({ x: pointX, y: pointY }, primitive.transformInverse);
    const b = { x: Math.abs((scale * primitive.width) / 2), y: Math.abs((scale * primitive.height) / 2) };
    const absPx = Math.abs(p.x);
    const absPy = Math.abs(p.y);
    const edgeDistX = Math.abs(absPx - b.x);
    const edgeDistY = Math.abs(absPy - b.y);
    // pick radius based on quadrant
    const radius = p.x > 0
        ? p.y > 0
            ? primitive.radiusTopRight
            : primitive.radiusBottomRight
        : p.y > 0
            ? primitive.radiusTopLeft
            : primitive.radiusBottomLeft;
    // corner center relative to this radius
    const cx = b.x - radius;
    const cy = b.y - radius;
    if (absPx > cx && absPy > cy) {
        // if on corner
        const centerDist = Math.hypot(absPx - cx, absPy - cy);
        const cornerDist = Math.abs(centerDist - radius);
        const isInside = centerDist < radius;
        const dist = cornerDist * (isInside ? -1 : 1);
        return { dist };
    }
    else {
        // if on edge
        const edgeDist = Math.min(edgeDistX, edgeDistY);
        const isInside = absPx < b.x && absPy < b.y;
        const dist = edgeDist * (isInside ? -1 : 1);
        return { dist };
    }
}
function linePointDistance(primitive, pointX, pointY, scale) {
    const localP = vec2.applyMatrix3({ x: pointX, y: pointY }, primitive.transformInverse);
    const pa = { x: primitive.startX, y: primitive.startY };
    const pb = { x: primitive.endX, y: primitive.endY };
    const l = vec2.distance(pa, pb);
    const d = vec2
        .chain(pb)
        .sub(pa)
        .scale(1 / l)
        .xy();
    const q = vec2.sub(localP, vec2
        .chain(pa)
        .add(pb)
        .scale(1 / 2)
        .xy());
    const tx = q.x;
    const ty = q.y;
    q.x = Math.abs(d.x * tx + d.y * ty);
    q.y = Math.abs(-d.y * tx + d.x * ty);
    q.x -= l / 2;
    const dist = Math.min(Math.max(q.x, q.y), 0) + vec2.len(vec2.max(q, ZeroVector2)) - primitive.thickness / 2;
    return { dist: dist / scale };
    // TODO closestPoint
}
function unionPointDistance(primitive, pointX, pointY) {
    const localP = vec2.applyMatrix3({ x: pointX, y: pointY }, primitive.transformInverse);
    const px = localP.x;
    const py = localP.y;
    const { outPoint: closestPointA } = pointDistance(primitive.primitiveA, px, py);
    const { outPoint: closestPointB } = pointDistance(primitive.primitiveB, px, py);
    const worldP = { x: pointX, y: pointY };
    let closestPoint = closestPointA;
    if (closestPointA && closestPointB && vec2.distance(closestPointA, worldP) > vec2.distance(closestPointA, worldP)) {
        closestPoint = closestPointB;
    }
    if (!closestPoint) {
        // Should throw an error here?
        return { dist: 0, outPoint: undefined };
    }
    return { dist: vec2.distance(worldP, closestPoint), outPoint: closestPoint };
}
function differencePointDistance(primitive, pointX, pointY, scale) {
    const localP = vec2.applyMatrix3({ x: pointX, y: pointY }, primitive.transformInverse);
    const px = localP.x;
    const py = localP.y;
    return {
        dist: Math.max(pointDistance(primitive.primitiveA, px, py).dist, -pointDistance(primitive.primitiveB, px, py).dist) / scale,
    };
    // TODO closestPoint
}
function expansionPointDistance(primitive, pointX, pointY, scale) {
    const localP = vec2.applyMatrix3({ x: pointX, y: pointY }, primitive.transformInverse);
    const px = localP.x;
    const py = localP.y;
    const r = pointDistance(primitive.primitive, px, py);
    return {
        dist: r.dist - primitive.expansion / scale,
        // TODO
        outPoint: r.outPoint,
    };
}
//# sourceMappingURL=distance.js.map